import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import Autocomplete from './ui/autocomplete';
import { Progress } from './ui/progress';
import { 
  Users, 
  Target, 
  Star,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Settings,
  Zap,
  Crown,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Award,
  Lightbulb,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Eye,
  Edit,
  Download,
  RefreshCw,
  Filter,
  Search,
  UserCheck,
  Brain,
  Rocket,
  Diamond,
  Gem,
  Sparkles,
  FlameKindling
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// 9Box Position Configurations
const NINE_BOX_CONFIG = {
  "low_performance_low_potential": {
    label: "Questionável",
    description: "Performance e potencial baixos",
    color: "#dc2626",
    bgColor: "#fef2f2",
    borderColor: "#fecaca",
    icon: AlertTriangle,
    priority: "critical"
  },
  "low_performance_medium_potential": {
    label: "Inconsistente",
    description: "Baixa performance, potencial médio",
    color: "#ea580c",
    bgColor: "#fff7ed",
    borderColor: "#fed7aa",
    icon: TrendingUp,
    priority: "high"
  },
  "low_performance_high_potential": {
    label: "Diamante Bruto",
    description: "Baixa performance, alto potencial",
    color: "#d97706",
    bgColor: "#fffbeb",
    borderColor: "#fde68a",
    icon: Diamond,
    priority: "medium"
  },
  "medium_performance_low_potential": {
    label: "Sólido",
    description: "Performance adequada, potencial limitado",
    color: "#7c3aed",
    bgColor: "#faf5ff",
    borderColor: "#ddd6fe",
    icon: Shield,
    priority: "low"
  },
  "medium_performance_medium_potential": {
    label: "Núcleo",
    description: "Performance e potencial médios",
    color: "#2563eb",
    bgColor: "#eff6ff",
    borderColor: "#bfdbfe",
    icon: Users,
    priority: "medium"
  },
  "medium_performance_high_potential": {
    label: "Alto Potencial",
    description: "Performance média, alto potencial",
    color: "#0891b2",
    bgColor: "#ecfeff",
    borderColor: "#a5f3fc",
    icon: Rocket,
    priority: "high"
  },
  "high_performance_low_potential": {
    label: "Estrela Consistente",
    description: "Alta performance, potencial limitado",
    color: "#16a34a",
    bgColor: "#f0fdf4",
    borderColor: "#bbf7d0",
    icon: Star,
    priority: "low"
  },
  "high_performance_medium_potential": {
    label: "Jogador Chave",
    description: "Alta performance, potencial médio",
    color: "#059669",
    bgColor: "#ecfdf5",
    borderColor: "#a7f3d0",
    icon: Award,
    priority: "medium"
  },
  "high_performance_high_potential": {
    label: "Futuro Líder",
    description: "Alta performance e alto potencial",
    color: "#0d9488",
    bgColor: "#f0fdfa",
    borderColor: "#99f6e4",
    icon: Crown,
    priority: "critical"
  }
};

const NineBoxPage = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState('matrix');
  const [loading, setLoading] = useState(false);
  const allowedTabs = ['matrix', 'analytics', 'succession', 'individual', 'settings'];
  
  // Data states
  const [matrix, setMatrix] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [successionPlan, setSuccessionPlan] = useState([]);
  
  // Filter states
  const [selectedCycle, setSelectedCycle] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filterPosition, setFilterPosition] = useState('all');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedCycle) {
      loadMatrix();
      loadAnalytics();
    }
  }, [selectedCycle]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadEmployees(),
        loadCycles(),
        loadPositions()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Erro ao carregar dados iniciais');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/api/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadCycles = async () => {
    try {
      const response = await axios.get(`${API}/api/performance/cycles`);
      setCycles(response.data);
      
      // Auto-select the most recent completed cycle
      const completedCycles = response.data.filter(c => c.status === 'completed');
      if (completedCycles.length > 0) {
        setSelectedCycle(completedCycles[0].id);
      }
    } catch (error) {
      console.error('Error loading cycles:', error);
    }
  };

  const loadPositions = async () => {
    try {
      const params = selectedCycle ? `?cycle_id=${selectedCycle}` : '';
      const response = await axios.get(`${API}/api/nine-box/positions${params}`);
      setPositions(response.data);
    } catch (error) {
      console.error('Error loading positions:', error);
    }
  };

  const loadMatrix = async () => {
    try {
      const params = selectedCycle ? `?cycle_id=${selectedCycle}` : '';
      const response = await axios.get(`${API}/api/nine-box/matrix${params}`);
      setMatrix(response.data);
    } catch (error) {
      console.error('Error loading matrix:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const params = selectedCycle ? `?cycle_id=${selectedCycle}` : '';
      const response = await axios.get(`${API}/api/nine-box/analytics${params}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadSuccessionPlan = async () => {
    try {
      const response = await axios.get(`${API}/api/nine-box/succession-plan`);
      setSuccessionPlan(response.data);
    } catch (error) {
      console.error('Error loading succession plan:', error);
    }
  };

  const handleAutoPopulate = async () => {
    if (!selectedCycle) {
      toast.error('Selecione um ciclo de avaliação primeiro');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/nine-box/auto-populate/${selectedCycle}`);
      toast.success(`Matriz 9Box populada! ${response.data.created} criados, ${response.data.updated} atualizados`);
      
      // Reload data
      await Promise.all([loadMatrix(), loadAnalytics(), loadPositions()]);
    } catch (error) {
      console.error('Error auto-populating 9Box:', error);
      toast.error('Erro ao popular matriz 9Box automaticamente');
    } finally {
      setLoading(false);
    }
  };

  const formatPercentage = (value, total) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
  };

  const getEmployeeCard = (employee, position) => {
    const config = NINE_BOX_CONFIG[position.position] || NINE_BOX_CONFIG["medium_performance_medium_potential"];
    const Icon = config.icon;
    
    return (
      <div
        key={employee.id}
        className="relative group cursor-pointer"
        onClick={() => setSelectedEmployee({...employee, position})}
      >
        <div 
          className="p-3 rounded-lg border-2 shadow-sm hover:shadow-md transition-all transform hover:scale-105"
          style={{
            backgroundColor: config.bgColor,
            borderColor: config.borderColor
          }}
        >
          <div className="flex items-center space-x-2">
            <Icon className="h-4 w-4" style={{ color: config.color }} />
            <div className="flex-grow min-w-0">
              <p className="font-medium text-sm truncate">{employee.nome}</p>
              <p className="text-xs text-gray-600 truncate">{employee.cargo || 'Sem cargo'}</p>
            </div>
          </div>
          
          <div className="mt-2 flex justify-between text-xs">
            <span style={{ color: config.color }}>P: {position.performance_score?.toFixed(1)}</span>
            <span style={{ color: config.color }}>Pot: {position.potential_score?.toFixed(1)}</span>
          </div>
          
          {position.succession_ready && (
            <div className="absolute -top-1 -right-1">
              <Crown className="h-3 w-3 text-yellow-500" />
            </div>
          )}
          
          {position.retention_risk !== 'low' && (
            <div className="absolute top-0 left-0">
              <div className={`h-2 w-2 rounded-full ${
                position.retention_risk === 'critical' ? 'bg-red-500' :
                position.retention_risk === 'high' ? 'bg-orange-500' : 'bg-yellow-500'
              }`} />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Matriz 9Box</h1>
          <p className="text-gray-600 mt-2">
            Performance vs Potencial - Mapeamento estratégico de talentos
          </p>
        </div>
        <div className="flex space-x-2">
          <Select value={selectedCycle} onValueChange={setSelectedCycle}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Selecione o ciclo de avaliação" />
            </SelectTrigger>
            <SelectContent>
              {cycles.filter(c => c.status === 'completed').map(cycle => (
                <SelectItem key={cycle.id} value={cycle.id}>
                  {cycle.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={loadMatrix} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          {allowedTabs.includes('matrix') && <TabsTrigger value="matrix">Matriz 9Box</TabsTrigger>}
          {allowedTabs.includes('analytics') && <TabsTrigger value="analytics">Analytics</TabsTrigger>}
          {allowedTabs.includes('succession') && <TabsTrigger value="succession">Sucessão</TabsTrigger>}
          {allowedTabs.includes('individual') && <TabsTrigger value="individual">Individual</TabsTrigger>}
          {allowedTabs.includes('settings') && <TabsTrigger value="settings">Configurações</TabsTrigger>}
        </TabsList>

        {/* Matrix Tab */}
        {allowedTabs.includes('matrix') && (
        <TabsContent value="matrix">
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Funcionários</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{matrix?.summary?.total_employees || 0}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Futuros Líderes</CardTitle>
                  <Crown className="h-4 w-4 text-yellow-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600">
                    {matrix?.summary?.by_position?.high_performance_high_potential || 0}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Prontos p/ Sucessão</CardTitle>
                  <UserCheck className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {matrix?.summary?.succession_ready || 0}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Risco Alto</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {(matrix?.summary?.retention_risks?.high || 0) + (matrix?.summary?.retention_risks?.critical || 0)}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Controls */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Crown className="h-4 w-4 text-yellow-500" />
                      <span className="text-sm">Sucessão</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="h-2 w-2 rounded-full bg-red-500" />
                      <span className="text-sm">Risco Alto</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="h-2 w-2 rounded-full bg-orange-500" />
                      <span className="text-sm">Risco Médio</span>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleAutoPopulate}
                    disabled={!selectedCycle || loading}
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    {loading ? 'Populando...' : 'Auto Popular'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* 9Box Matrix */}
            {matrix && (
              <NineBoxMatrix 
                matrix={matrix} 
                onEmployeeClick={setSelectedEmployee}
              />
            )}
          </div>
        </TabsContent>
        )}

        {/* Analytics Tab */}
        {allowedTabs.includes('analytics') && (
        <TabsContent value="analytics">
          {analytics && (
            <AnalyticsDashboard 
              analytics={analytics}
              matrix={matrix}
            />
          )}
        </TabsContent>
        )}

        {/* Succession Tab */}
        {allowedTabs.includes('succession') && (
        <TabsContent value="succession">
          <SuccessionPlanning 
            successionPlan={successionPlan}
            onLoadSuccession={loadSuccessionPlan}
          />
        </TabsContent>
        )}

        {/* Individual Tab */}
        {allowedTabs.includes('individual') && (
        <TabsContent value="individual">
          <IndividualView 
            employees={employees}
            positions={positions}
            onPositionUpdate={loadPositions}
          />
        </TabsContent>
        )}

        {/* Settings Tab */}
        {allowedTabs.includes('settings') && (
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Configurações da Matriz 9Box</span>
              </CardTitle>
              <CardDescription>
                Configurações e personalização da matriz
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Configurações em desenvolvimento</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <EmployeeDetailModal 
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
          onUpdate={loadMatrix}
        />
      )}
    </div>
  );
};

// 9Box Matrix Component
const NineBoxMatrix = ({ matrix, onEmployeeClick }) => {
  const gridPositions = [
    // Row 3 (High Potential)
    ['low_performance_high_potential', 'medium_performance_high_potential', 'high_performance_high_potential'],
    // Row 2 (Medium Potential)  
    ['low_performance_medium_potential', 'medium_performance_medium_potential', 'high_performance_medium_potential'],
    // Row 1 (Low Potential)
    ['low_performance_low_potential', 'medium_performance_low_potential', 'high_performance_low_potential']
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>Matriz Performance vs Potencial</span>
          </CardTitle>
          <Badge variant="outline">
            {matrix.summary.total_employees} funcionários mapeados
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Axis Labels */}
          <div className="absolute -left-16 top-1/2 transform -translate-y-1/2 -rotate-90">
            <span className="text-sm font-medium text-gray-700">POTENCIAL</span>
          </div>
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
            <span className="text-sm font-medium text-gray-700">PERFORMANCE</span>
          </div>
          
          {/* Potential Labels */}
          <div className="absolute -left-12 flex flex-col justify-between h-full py-4">
            <span className="text-xs text-gray-600">Alto</span>
            <span className="text-xs text-gray-600">Médio</span>
            <span className="text-xs text-gray-600">Baixo</span>
          </div>
          
          {/* Performance Labels */}
          <div className="absolute -bottom-6 flex justify-between w-full px-4">
            <span className="text-xs text-gray-600">Baixa</span>
            <span className="text-xs text-gray-600">Média</span>
            <span className="text-xs text-gray-600">Alta</span>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-3 gap-2 p-4">
            {gridPositions.map((row, rowIndex) => 
              row.map((positionKey, colIndex) => {
                const config = NINE_BOX_CONFIG[positionKey];
                const positionData = matrix.positions[positionKey];
                const employees = positionData?.employees || [];
                const Icon = config.icon;

                return (
                  <div
                    key={positionKey}
                    className="relative min-h-[200px] p-4 rounded-lg border-2 transition-all hover:shadow-lg"
                    style={{
                      backgroundColor: config.bgColor,
                      borderColor: config.borderColor
                    }}
                  >
                    {/* Quadrant Header */}
                    <div className="flex items-center space-x-2 mb-3 pb-2 border-b border-gray-200">
                      <Icon className="h-5 w-5" style={{ color: config.color }} />
                      <div>
                        <h4 className="font-semibold text-sm" style={{ color: config.color }}>
                          {config.label}
                        </h4>
                        <p className="text-xs text-gray-600">{employees.length} funcionários</p>
                      </div>
                    </div>

                    {/* Employees */}
                    <div className="space-y-2 max-h-36 overflow-y-auto">
                      {employees.map(employeeData => (
                        <div
                          key={employeeData.employee.id}
                          className="p-2 bg-white rounded border cursor-pointer hover:shadow-sm transition-all"
                          onClick={() => onEmployeeClick({...employeeData.employee, position: employeeData})}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-grow min-w-0">
                              <p className="font-medium text-xs truncate">{employeeData.employee.nome}</p>
                              <p className="text-xs text-gray-600 truncate">{employeeData.employee.cargo || 'Sem cargo'}</p>
                            </div>
                            
                            <div className="flex items-center space-x-1 ml-2">
                              {employeeData.succession_ready && (
                                <Crown className="h-3 w-3 text-yellow-500" />
                              )}
                              {employeeData.retention_risk !== 'low' && (
                                <div className={`h-2 w-2 rounded-full ${
                                  employeeData.retention_risk === 'critical' ? 'bg-red-500' :
                                  employeeData.retention_risk === 'high' ? 'bg-orange-500' : 'bg-yellow-500'
                                }`} />
                              )}
                            </div>
                          </div>
                          
                          <div className="flex justify-between text-xs mt-1 text-gray-500">
                            <span>P: {employeeData.performance_score?.toFixed(1)}</span>
                            <span>Pot: {employeeData.potential_score?.toFixed(1)}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {employees.length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Nenhum funcionário</p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Analytics Dashboard Component
const AnalyticsDashboard = ({ analytics, matrix }) => {
  const total = analytics.overview.total_employees;
  
  const formatPercentage = (value, total) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estrelas</CardTitle>
            <Crown className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {analytics.talent_insights.stars}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatPercentage(analytics.talent_insights.stars, total)}% do total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Alto Potencial</CardTitle>
            <Rocket className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {analytics.talent_insights.future_leaders}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatPercentage(analytics.talent_insights.future_leaders, total)}% do total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Precisam Desenvolver</CardTitle>
            <TrendingUp className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {analytics.talent_insights.development_needed}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatPercentage(analytics.talent_insights.development_needed, total)}% do total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sucessão</CardTitle>
            <UserCheck className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {analytics.talent_insights.succession_ready}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatPercentage(analytics.talent_insights.succession_ready, total)}% do total
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <PieChart className="h-5 w-5" />
              <span>Distribuição por Performance</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 bg-green-500 rounded"></div>
                  <span className="text-sm">Alta Performance</span>
                </div>
                <div className="text-sm font-medium">
                  {analytics.distribution.by_performance.high} ({formatPercentage(analytics.distribution.by_performance.high, total)}%)
                </div>
              </div>
              <Progress 
                value={parseFloat(formatPercentage(analytics.distribution.by_performance.high, total))} 
                className="h-2"
              />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 bg-blue-500 rounded"></div>
                  <span className="text-sm">Performance Média</span>
                </div>
                <div className="text-sm font-medium">
                  {analytics.distribution.by_performance.medium} ({formatPercentage(analytics.distribution.by_performance.medium, total)}%)
                </div>
              </div>
              <Progress 
                value={parseFloat(formatPercentage(analytics.distribution.by_performance.medium, total))} 
                className="h-2"
              />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 bg-red-500 rounded"></div>
                  <span className="text-sm">Performance Baixa</span>
                </div>
                <div className="text-sm font-medium">
                  {analytics.distribution.by_performance.low} ({formatPercentage(analytics.distribution.by_performance.low, total)}%)
                </div>
              </div>
              <Progress 
                value={parseFloat(formatPercentage(analytics.distribution.by_performance.low, total))} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Brain className="h-5 w-5" />
              <span>Distribuição por Potencial</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 bg-purple-500 rounded"></div>
                  <span className="text-sm">Alto Potencial</span>
                </div>
                <div className="text-sm font-medium">
                  {analytics.distribution.by_potential.high} ({formatPercentage(analytics.distribution.by_potential.high, total)}%)
                </div>
              </div>
              <Progress 
                value={parseFloat(formatPercentage(analytics.distribution.by_potential.high, total))} 
                className="h-2"
              />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 bg-blue-500 rounded"></div>
                  <span className="text-sm">Potencial Médio</span>
                </div>
                <div className="text-sm font-medium">
                  {analytics.distribution.by_potential.medium} ({formatPercentage(analytics.distribution.by_potential.medium, total)}%)
                </div>
              </div>
              <Progress 
                value={parseFloat(formatPercentage(analytics.distribution.by_potential.medium, total))} 
                className="h-2"
              />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 bg-gray-500 rounded"></div>
                  <span className="text-sm">Potencial Limitado</span>
                </div>
                <div className="text-sm font-medium">
                  {analytics.distribution.by_potential.low} ({formatPercentage(analytics.distribution.by_potential.low, total)}%)
                </div>
              </div>
              <Progress 
                value={parseFloat(formatPercentage(analytics.distribution.by_potential.low, total))} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      {analytics.recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Lightbulb className="h-5 w-5" />
              <span>Recomendações Estratégicas</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.recommendations.map((rec, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  rec.priority === 'high' ? 'border-red-500 bg-red-50' :
                  rec.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                  'border-blue-500 bg-blue-50'
                }`}>
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className={`h-5 w-5 mt-0.5 ${
                      rec.priority === 'high' ? 'text-red-500' :
                      rec.priority === 'medium' ? 'text-yellow-500' :
                      'text-blue-500'
                    }`} />
                    <div className="flex-grow">
                      <h4 className="font-semibold text-sm mb-1">{rec.message}</h4>
                      <p className="text-sm text-gray-600">{rec.action}</p>
                    </div>
                    <Badge variant={rec.priority === 'high' ? 'destructive' : rec.priority === 'medium' ? 'default' : 'secondary'}>
                      {rec.priority === 'high' ? 'Prioridade Alta' : rec.priority === 'medium' ? 'Prioridade Média' : 'Prioridade Baixa'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Succession Planning Component
const SuccessionPlanning = ({ successionPlan, onLoadSuccession }) => {
  useEffect(() => {
    onLoadSuccession();
  }, [onLoadSuccession]);

  const groupedByReadiness = successionPlan.reduce((acc, item) => {
    if (!acc[item.readiness]) acc[item.readiness] = [];
    acc[item.readiness].push(item);
    return acc;
  }, {});

  const readinessConfig = {
    immediate: { label: 'Prontos Agora', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    short_term: { label: '1-2 Anos', color: 'bg-blue-100 text-blue-800', icon: Clock },
    medium_term: { label: '2-3 Anos', color: 'bg-purple-100 text-purple-800', icon: TrendingUp }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Crown className="h-5 w-5" />
            <span>Plano de Sucessão</span>
          </CardTitle>
          <CardDescription>
            Identificação de sucessores baseada na matriz 9Box
          </CardDescription>
        </CardHeader>
        <CardContent>
          {Object.entries(groupedByReadiness).map(([readiness, employees]) => {
            const config = readinessConfig[readiness];
            const Icon = config.icon;
            
            return (
              <div key={readiness} className="mb-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{config.label}</h3>
                  <Badge className={config.color}>{employees.length} funcionários</Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {employees.map((emp, index) => (
                    <Card key={index} className="border-l-4 border-l-green-500">
                      <CardContent className="pt-4">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <Crown className="h-4 w-4 text-yellow-500" />
                            <h4 className="font-semibold">{emp.employee.nome}</h4>
                          </div>
                          
                          <div className="text-sm text-gray-600">
                            <strong>Cargo atual:</strong> {emp.employee.cargo || 'Não informado'}
                          </div>
                          
                          <div className="flex justify-between text-sm">
                            <span><strong>Performance:</strong> {emp.position_data.performance_score?.toFixed(1)}</span>
                            <span><strong>Potencial:</strong> {emp.position_data.potential_score?.toFixed(1)}</span>
                          </div>
                          
                          <div className="text-sm">
                            <strong>Cargos recomendados:</strong>
                            <ul className="list-disc list-inside mt-1 text-gray-600">
                              {emp.recommended_roles.slice(0, 3).map((role, roleIndex) => (
                                <li key={roleIndex}>{role}</li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="text-sm">
                            <strong>Desenvolvimento necessário:</strong>
                            <ul className="list-disc list-inside mt-1 text-gray-600">
                              {emp.development_needs.slice(0, 2).map((need, needIndex) => (
                                <li key={needIndex}>{need}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
          
          {successionPlan.length === 0 && (
            <div className="text-center py-8">
              <Crown className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum plano de sucessão encontrado</p>
              <p className="text-sm text-gray-400 mt-2">
                Populate a matriz 9Box primeiro para gerar planos de sucessão
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Individual View Component
const IndividualView = ({ employees, positions, onPositionUpdate }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employeePosition, setEmployeePosition] = useState(null);

  useEffect(() => {
    if (selectedEmployee) {
      const position = positions.find(p => p.employee_id === selectedEmployee);
      setEmployeePosition(position);
    }
  }, [selectedEmployee, positions]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Visualização Individual</span>
          </CardTitle>
          <CardDescription>
            Analise a posição individual de cada funcionário na matriz 9Box
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="employee-select">Selecione o Funcionário</Label>
              <Autocomplete
                value={selectedEmployee}
                onValueChange={setSelectedEmployee}
                options={employees}
                getOptionValue={(emp) => emp.id}
                getOptionLabel={(emp) => `${emp.nome} - ${emp.cargo || 'Sem cargo'}`}
                placeholder="Escolha um funcionário"
                emptyMessage="Nenhum funcionário encontrado"
              />
            </div>

            {employeePosition && (
              <div className="mt-6">
                <IndividualEmployeeAnalysis 
                  employee={employees.find(e => e.id === selectedEmployee)}
                  position={employeePosition}
                  onUpdate={onPositionUpdate}
                />
              </div>
            )}

            {selectedEmployee && !employeePosition && (
              <div className="text-center py-8">
                <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Funcionário não encontrado na matriz 9Box</p>
                <p className="text-sm text-gray-400 mt-2">
                  Execute o preenchimento automático primeiro
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Individual Employee Analysis Component
const IndividualEmployeeAnalysis = ({ employee, position, onUpdate }) => {
  const config = NINE_BOX_CONFIG[position.position];
  const Icon = config.icon;

  return (
    <div className="space-y-6">
      {/* Employee Header */}
      <Card className="border-l-4" style={{ borderLeftColor: config.color }}>
        <CardContent className="pt-4">
          <div className="flex items-center space-x-4">
            <div 
              className="p-3 rounded-full"
              style={{ backgroundColor: config.bgColor }}
            >
              <Icon className="h-6 w-6" style={{ color: config.color }} />
            </div>
            <div className="flex-grow">
              <h3 className="text-xl font-semibold">{employee.nome}</h3>
              <p className="text-gray-600">{employee.cargo || 'Cargo não informado'}</p>
              <div className="flex items-center space-x-4 mt-2">
                <Badge style={{ backgroundColor: config.color, color: 'white' }}>
                  {config.label}
                </Badge>
                {position.succession_ready && (
                  <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                    <Crown className="h-3 w-3 mr-1" />
                    Sucessão
                  </Badge>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Performance</div>
              <div className="text-2xl font-bold" style={{ color: config.color }}>
                {position.performance_score?.toFixed(1)}
              </div>
              <div className="text-sm text-gray-600 mt-2">Potencial</div>
              <div className="text-2xl font-bold" style={{ color: config.color }}>
                {position.potential_score?.toFixed(1)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analysis Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Análise de Posição</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Descrição</h4>
                <p className="text-gray-600">{config.description}</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Ação Recomendada</h4>
                <p className="text-gray-600">{NINE_BOX_CONFIG[position.position]?.action || 'Manter desenvolvimento contínuo'}</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Prioridade</h4>
                <Badge variant={
                  config.priority === 'critical' ? 'destructive' :
                  config.priority === 'high' ? 'default' :
                  config.priority === 'medium' ? 'secondary' : 'outline'
                }>
                  {config.priority === 'critical' ? 'Crítica' :
                   config.priority === 'high' ? 'Alta' :
                   config.priority === 'medium' ? 'Média' : 'Baixa'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Informações Adicionais</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Risco de Retenção</h4>
                <Badge variant={
                  position.retention_risk === 'critical' ? 'destructive' :
                  position.retention_risk === 'high' ? 'default' :
                  position.retention_risk === 'medium' ? 'secondary' : 'outline'
                }>
                  {position.retention_risk === 'critical' ? 'Crítico' :
                   position.retention_risk === 'high' ? 'Alto' :
                   position.retention_risk === 'medium' ? 'Médio' : 'Baixo'}
                </Badge>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Última Revisão</h4>
                <p className="text-gray-600">
                  {new Date(position.last_review_date).toLocaleDateString('pt-BR')}
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Próxima Revisão</h4>
                <p className="text-gray-600">
                  {new Date(position.next_review_date).toLocaleDateString('pt-BR')}
                </p>
              </div>

              {position.manager_notes && (
                <div>
                  <h4 className="font-semibold mb-2">Observações do Gestor</h4>
                  <p className="text-gray-600 text-sm">{position.manager_notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Employee Detail Modal Component
const EmployeeDetailModal = ({ employee, onClose, onUpdate }) => {
  const position = employee.position;
  const config = NINE_BOX_CONFIG[position.position];
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex items-center space-x-3">
              <div 
                className="p-2 rounded-lg"
                style={{ backgroundColor: config.bgColor }}
              >
                <Icon className="h-5 w-5" style={{ color: config.color }} />
              </div>
              <div>
                <CardTitle>{employee.nome}</CardTitle>
                <CardDescription>{employee.cargo || 'Cargo não informado'}</CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={onClose}>
              ×
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold">Posição 9Box</Label>
                <Badge 
                  className="mt-1 block w-fit"
                  style={{ backgroundColor: config.color, color: 'white' }}
                >
                  {config.label}
                </Badge>
              </div>
              <div>
                <Label className="text-sm font-semibold">Scores</Label>
                <div className="flex space-x-4 mt-1">
                  <span className="text-sm">P: {position.performance_score?.toFixed(1)}</span>
                  <span className="text-sm">Pot: {position.potential_score?.toFixed(1)}</span>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">Descrição</Label>
              <p className="text-sm text-gray-600 mt-1">{config.description}</p>
            </div>

            <div>
              <Label className="text-sm font-semibold">Ação Recomendada</Label>
              <p className="text-sm text-gray-600 mt-1">{config.action}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold">Sucessão</Label>
                <Badge 
                  variant={position.succession_ready ? 'default' : 'outline'}
                  className="mt-1 block w-fit"
                >
                  {position.succession_ready ? 'Pronto' : 'Não pronto'}
                </Badge>
              </div>
              <div>
                <Label className="text-sm font-semibold">Risco de Retenção</Label>
                <Badge 
                  variant={
                    position.retention_risk === 'critical' ? 'destructive' :
                    position.retention_risk === 'high' ? 'default' :
                    position.retention_risk === 'medium' ? 'secondary' : 'outline'
                  }
                  className="mt-1 block w-fit"
                >
                  {position.retention_risk === 'critical' ? 'Crítico' :
                   position.retention_risk === 'high' ? 'Alto' :
                   position.retention_risk === 'medium' ? 'Médio' : 'Baixo'}
                </Badge>
              </div>
            </div>

            {position.manager_notes && (
              <div>
                <Label className="text-sm font-semibold">Observações do Gestor</Label>
                <p className="text-sm text-gray-600 mt-1">{position.manager_notes}</p>
              </div>
            )}

            {position.action_plan && (
              <div>
                <Label className="text-sm font-semibold">Plano de Ação</Label>
                <p className="text-sm text-gray-600 mt-1">{position.action_plan}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NineBoxPage;